import { useState } from 'react';
import { Box, Dialog, IconButton, Typography } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ZoomInIcon from '@mui/icons-material/ZoomIn';

interface VirtualPreviewProps {
  /** URL del render d'ambiente "Virtual" (BigBox) del pacchetto selezionato. */
  src: string;
  /** Etichetta (es. nome pacchetto) per alt/zoom. */
  label?: string | undefined;
}

/**
 * Anteprima render d'ambiente ("BigBox") del pacchetto selezionato, mostrata nella colonna sinistra
 * della fase GIORNO E NOTTE al posto della planimetria (come il legacy). Click → zoom a tutto schermo.
 */
export function VirtualPreview({ src, label }: VirtualPreviewProps): JSX.Element {
  const [zoomOpen, setZoomOpen] = useState(false);
  const alt = label ? `Anteprima ambiente — ${label}` : 'Anteprima ambiente';

  return (
    <Box>
      <Typography
        variant="overline"
        sx={{ color: 'text.secondary', letterSpacing: '0.08em', fontWeight: 600 }}
      >
        Anteprima ambiente
      </Typography>

      <Box
        role="button"
        tabIndex={0}
        aria-label="Ingrandisci l'anteprima"
        onClick={() => setZoomOpen(true)}
        onKeyDown={(e) => {
          if (e.key === 'Enter' || e.key === ' ') setZoomOpen(true);
        }}
        sx={{
          position: 'relative',
          mt: 1,
          cursor: 'zoom-in',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 1,
          overflow: 'hidden',
          bgcolor: 'background.paper',
          '&:focus-visible': { outline: '2px solid', outlineColor: 'primary.main' },
        }}
      >
        <Box
          component="img"
          src={src}
          alt={alt}
          sx={{ display: 'block', width: '100%', height: 'auto' }}
        />
        <Box
          sx={{
            position: 'absolute',
            bottom: 8,
            right: 8,
            bgcolor: 'rgba(0,0,0,0.55)',
            color: '#fff',
            borderRadius: '50%',
            width: 36,
            height: 36,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
          }}
        >
          <ZoomInIcon fontSize="small" />
        </Box>
      </Box>

      <Dialog open={zoomOpen} onClose={() => setZoomOpen(false)} maxWidth="lg" fullWidth>
        <Box sx={{ position: 'relative', bgcolor: '#fff' }}>
          <IconButton
            aria-label="Chiudi"
            onClick={() => setZoomOpen(false)}
            sx={{ position: 'absolute', top: 8, right: 8, bgcolor: 'rgba(0,0,0,0.06)' }}
          >
            <CloseIcon />
          </IconButton>
          <Box
            component="img"
            src={src}
            alt={alt}
            sx={{ display: 'block', width: '100%', height: 'auto' }}
          />
        </Box>
      </Dialog>
    </Box>
  );
}
